import React, { useCallback, useEffect, useState } from "react";
import { Container, Spinner } from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { toast } from "react-toastify";

import BreadCrumb from "Components/Common/BreadCrumb";
import PSEAAssessmentForm from "./PSEAAssessmentForm";
import { buildPseaFormData } from "./buildPseaFormData";
import { coreStandardsValidationSchema } from "./pseaValidation";
import { getPseaAssessment } from "common/services/psea-assessments.service";
import { normalize } from "common/lib/normalize";
import { usePageActivity } from "Components/Hooks/UsePageActivity";
import { hydrateCoreStandards } from "./pseaHydrate";
import { usePseaUpdater } from "./usePseaUpdater";
import { today } from "./pseaDates";

const UpdatePSEAAssessment = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: itemId } = useParams();
  usePageActivity(location.pathname, "Update PSEA Assessment", itemId);

  const { update } = usePseaUpdater();
  const [loading, setLoading] = useState<boolean>(false);
  const [fetching, setFetching] = useState<boolean>(true);
  const [record, setRecord] = useState<any>(null);

  const fetchData = useCallback(async () => {
    setFetching(true);
    try {
      const response = await getPseaAssessment(itemId, {});
      setRecord(normalize(response.data));
    } catch (e) {
      toast.error("Error while loading PSEA assessment");
    } finally {
      setFetching(false);
    }
  }, [itemId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      title: record?.title || "",
      contractor: {
        value: record?.contractor?.id || "",
        label: record?.contractor?.contractorName || "",
      },
      agency: {
        value: record?.agency?.id || "",
        label: record?.agency?.name || "",
      },
      dateOfAssessment:
        (record?.dateOfAssessment && new Date(record?.dateOfAssessment)) ||
        today(),
      enteredByName: [record?.createdby?.firstName, record?.createdby?.lastName]
        .filter(Boolean)
        .join(" "),
      enteredByEmail: record?.createdby?.email || "",
      justification: "",
      coreStandards: hydrateCoreStandards(record),
    },
    validationSchema: Yup.object({
      // The overall rating is derived from the core standards.
      justification: Yup.string()
        .trim()
        .required("Please provide a justification for this update"),
      coreStandards: coreStandardsValidationSchema,
    }),
    onSubmit: async (values: any) => {
      if (!update) return;
      const formData = buildPseaFormData(values, {
        historyDescription: "Assessment updated",
      });
      setLoading(true);
      try {
        await update({ id: itemId, formData });
        toast.success("PSEA assessment updated successfully");
        navigate(`/assessments/psea-assessments/${itemId}`);
      } catch (error: any) {
        toast.error(
          error?.response?.data?.error?.message ||
            error?.message ||
            "Error while updating PSEA assessment"
        );
      } finally {
        setLoading(false);
      }
    },
  });

  document.title = "Update PSEA Assessment | CIMS";

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title="Update PSEA Assessment"
          back
          pageTitles={[
            { title: "Dashboard", link: "/dashboard" },
            {
              title: "PSEA Assessments",
              link: "/assessments/psea-assessments",
            },
          ]}
        />
        {fetching ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
          </div>
        ) : (
          <PSEAAssessmentForm
            validation={validation}
            loading={loading}
            isUpdate
            disabled={!update}
            onCancel={() => navigate(`/assessments/psea-assessments/${itemId}`)}
          />
        )}
      </Container>
    </div>
  );
};

export default UpdatePSEAAssessment;
